import { FileText, Users, Eye, Check, CreditCard, Building2, Globe, MapPin, Phone } from 'lucide-react';
import { cn } from "@shared/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@shared/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@shared/ui/alert-dialog";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@shared/ui/use-toast';
import { useCurrency, useTenantId } from '@shared/selectors/globalSelectors';
import { usePostAddSubscription } from '../hooks';

// type PlanType = 'billing' | 'workforce' | 'utility';

interface PlanCardProps {
  type: string;
  title: string;
  meterCount: string;
  pricePerMeter: number;
  yearlyPricePerMeter?: number;
  monthlyCost: string;
  billingCycle?: 'monthly' | 'yearly';
  status: 'active' | 'inactive' | 'trial';
  className?: string;
  onView?: () => void;
  onEdit?: () => void;
  onContactSales?: () => void;
  keyDifferentiators?: string[];
  features?: string[];
  regions?: string[];
  countries?: string[];
  description?: string;
  maxMeters?: number;
  isActive?: boolean;
  showContactSales?: boolean;
  meterDescription?: string;
}

const PlanCard = ({
  type,
  title,
  meterCount,
  pricePerMeter,
  yearlyPricePerMeter,
  monthlyCost,
  billingCycle = 'monthly',
  status,
  className,
  onView,
  onEdit,
  onContactSales,
  keyDifferentiators = [],
  features = [],
  regions = [],
  countries = [],
  description,
  maxMeters,
  isActive = true,
  showContactSales = false,
  meterDescription
}: PlanCardProps) => {

  const currency = useCurrency();
  const tenantId = useTenantId();
  const [showDetails, setShowDetails] = useState(false);
  const [showContactSalesDialog, setShowContactSalesDialog] = useState(false);
  const navigate = useNavigate();
  const {mutate: addSubscription} = usePostAddSubscription();
  const getIcon = () => {
    switch (type) {
      case 'billing':
        return <FileText className="text-smart360-primary" />;
      case 'workforce':
        return <Users className="text-smart360-residential" />;
      case 'utility':
        return <Building2 className="text-smart360-commercial" />;
      default:
        return <FileText className="text-smart360-primary" />;
    }
  };

  const getBgClass = () => {
    switch (type) {
      case 'billing':
        return 'bg-gradient-to-br from-white to-blue-50';
      case 'workforce':
        return 'bg-gradient-to-br from-white to-amber-50';
      case 'utility':
        return 'bg-gradient-to-br from-white to-green-50';
      default:
        return 'bg-white';
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'trial':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return '';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return isActive ? 'Available' : 'Inactive';
      case 'trial':
        return 'Included in Trial';
      default:
        return '';
    }
  };

  const getPlanFeatures = () => {
    const billingFeatures = ["Complete billing automation", "Customer information system", "Self-service customer portal", "Standard usage reports"];
    const workforceFeatures = ["Service order management", "Field worker scheduling", "Task management", "Mobile app access"];
    const utilityFeatures = ["Multi-utility management", "Real-time monitoring", "Regulatory compliance", "Data encryption"];
    switch (type) {
      case 'billing':
        return billingFeatures;
      case 'workforce':
        return ["All Smart Billing features", ...workforceFeatures];
      case 'utility':
        return ["All Smart Workforce features", ...utilityFeatures];
      default:
        return [];
    }
  };

  const planFeatures = features.length > 0 ? features : getPlanFeatures();

  const handleCloseDetails = () => {
    setShowDetails(false);
  };

  // const formatCost = (cost: number) => {
  //   return cost(undefined, {
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2
  //   });
  // };

  const displayCost = billingCycle === 'yearly' ? monthlyCost : monthlyCost;
  const costLabel = billingCycle === 'yearly' ? 'Annual Cost' : 'Monthly Cost';
  const costPeriod = billingCycle === 'yearly' ? '/year' : '/month';

  const handleContactSales = () => {
    setShowContactSalesDialog(false);
    addSubscription({
      tenantId,
      title,
      description,
      priority: "high"
    }, {
      onSuccess: () => {
        onContactSales();
        // Show success toast
        toast({
          title: "Support Request Submitted",
          description: "Your request has been submitted successfully. You can track its progress using the 'Track Requests' button.",
        });
      }
    })
  };

  return <>
      <div className={cn("rounded-xl border border-smart360-border shadow-sm hover:shadow-md transition-all duration-300", getBgClass(), !isActive && "opacity-60", className)}>
        <div className="p-5">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                {getIcon()}
              </div>
              <div className="ml-3">
                <h3 className="font-semibold">{title}</h3>
                {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
              </div>
            </div>
          </div>
          
          {/* Enhanced Pricing section */}
          <div className="space-y-3 mb-4">
            <div className="rounded-lg bg-white bg-opacity-60 p-3 border border-smart360-border">
              <div className="text-xs text-gray-500 mb-1">Price per Meter</div>
              <div className="font-semibold text-lg text-smart360-primary">
                $ {pricePerMeter?.toFixed(2)}
                <span className="text-sm font-normal text-gray-500">/month</span>
              </div>
              {yearlyPricePerMeter && <div className="text-sm text-gray-600">
                  Yearly: $ {yearlyPricePerMeter?.toFixed(2)}/month
                </div>}
              <div className="text-xs text-gray-600 mt-1">
                {meterDescription || `For $$ ${meterCount} meters`}
              </div>
            </div>
            
            <div className="rounded-lg bg-white bg-opacity-60 p-3 border border-smart360-border">
              <div className="text-xs text-gray-500 mb-1">{costLabel}</div>
              <div className="font-semibold text-lg">
                $ {displayCost}
                <span className="text-sm font-normal text-gray-500">{costPeriod}</span>
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Based on {meterCount} @ ${pricePerMeter?.toFixed(2)}/meter
              </div>
              {billingCycle === 'yearly' && yearlyPricePerMeter && <div className="text-xs text-green-600 mt-1">
                  Save amount annually
                </div>}
            </div>
          </div>

          {/* Regions and Countries section */}
          {(regions.length > 0 || countries.length > 0) && <div className="bg-white bg-opacity-60 rounded-lg p-3 border border-smart360-border mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Globe size={16} />
                Availability
              </div>
              {regions.length > 0 && <div className="mb-2">
                  <div className="text-xs text-gray-500 mb-1">Regions:</div>
                  <div className="flex flex-wrap gap-1">
                    {regions.slice(0, 3).map((region, index) => <Badge key={index} variant="outline" className="text-xs">
                        {region}
                      </Badge>)}
                    {regions.length > 3 && <Badge variant="outline" className="text-xs">
                        +{regions.length - 3} more
                      </Badge>}
                  </div>
                </div>}
              {countries.length > 0 && <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <MapPin size={12} />
                    Countries:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {countries.slice(0, 3).map((country, index) => <Badge key={index} variant="secondary" className="text-xs">
                        {country}
                      </Badge>)}
                    {countries.length > 3 && <Badge variant="secondary" className="text-xs">
                        +{countries.length - 3} more
                      </Badge>}
                  </div>
                </div>}
            </div>}
          
          {/* Key Differentiators section */}
          {keyDifferentiators.length > 0 && <div className="bg-white bg-opacity-60 rounded-lg p-3 border border-smart360-border mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Key Features</div>
              <ul className="space-y-2">
                {keyDifferentiators.slice(0, 4).map((differentiator, index) => <li key={index} className="text-sm flex items-start">
                    <Check size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{differentiator}</span>
                  </li>)}
              </ul>
            </div>}

          {/* Features section */}
          {planFeatures.length > 0}
          
          <div className="flex space-x-2 mt-4">
            
            {showContactSales ? <AlertDialog open={showContactSalesDialog} onOpenChange={setShowContactSalesDialog}>
                <AlertDialogTrigger asChild>
                  <Button className="flex-1">
                    <Phone size={16} className="mr-1" />
                    Contact Sales
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-md">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <Phone size={20} className="text-blue-600" />
                      Contact Sales Team
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-left">
                      Ready to discuss the Smart Utility plan? Our sales team will reach out to you within 24 hours to help you get started with enterprise-grade features and custom pricing.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleContactSales} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      Contact Sales
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog> : onEdit ? <Button onClick={onEdit} className="flex-1" disabled={!isActive}>
                {status === 'active' ? <>
                    <Check size={16} className="mr-1" />
                    Active
                  </> : <>
                    <CreditCard size={16} className="mr-1" />
                    Activate
                  </>}
              </Button> : <Button disabled className="flex-1 bg-green-500">
                <Check size={16} className="mr-1" />
                Current Plan
              </Button>}
          </div>
        </div>
      </div>

      <Dialog open={showDetails} onOpenChange={handleCloseDetails}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center">
                {getIcon()}
              </div>
              {title} Plan Details
            </DialogTitle>
            <DialogDescription>
              Comprehensive overview of plan features and pricing structure
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                <h4 className="font-medium mb-2 text-gray-700">Pricing Structure</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Monthly Price:</span>
                    <span className="font-medium">$ {pricePerMeter?.toFixed(2)}/meter</span>
                  </div>
                  {yearlyPricePerMeter && <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Yearly Price:</span>
                      <span className="font-medium">$ {yearlyPricePerMeter?.toFixed(2)}/meter</span>
                    </div>}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Meter Count:</span>
                    <span className="font-medium">{meterCount}</span>
                  </div>
                  {maxMeters && <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Max Meters:</span>
                      <span className="font-medium">{maxMeters}</span>
                    </div>}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Example Cost:</span>
                    <span className="font-medium">{costPeriod}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                <h4 className="font-medium mb-2 text-gray-700">Plan Status</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={cn("px-2 py-1 text-xs font-medium rounded-full border", getStatusClass())}>
                      {getStatusText()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {description || (status === 'active' ? 'Your plan is currently active and all features are available.' : status === 'trial' ? 'This plan is included in your trial period.' : 'This plan is available for activation.')}
                  </p>
                </div>
              </div>
            </div>

            {/* Regional Availability */}
            {(regions.length > 0 || countries.length > 0) && <div className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                <h4 className="font-medium mb-3 text-gray-700 flex items-center gap-2">
                  <Globe size={16} />
                  Regional Availability
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {regions.length > 0 && <div>
                      <h5 className="text-sm font-medium text-gray-600 mb-2">Available Regions</h5>
                      <div className="flex flex-wrap gap-2">
                        {regions.map((region, idx) => <Badge key={idx} variant="outline" className="text-xs">
                            {region}
                          </Badge>)}
                      </div>
                    </div>}
                  {countries.length > 0 && <div>
                      <h5 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                        <MapPin size={14} />
                        Available Countries
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {countries.map((country, idx) => <Badge key={idx} variant="secondary" className="text-xs">
                            {country}
                          </Badge>)}
                      </div>
                    </div>}
                </div>
              </div>}

            {keyDifferentiators.length > 0 && <div className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                <h4 className="font-medium mb-3 text-gray-700">Key Differentiators</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {keyDifferentiators.map((differentiator, idx) => <div key={idx} className="flex items-start gap-2">
                      <Check size={16} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{differentiator}</span>
                    </div>)}
                </div>
              </div>}

            <div className="p-4 rounded-lg bg-gray-50 border border-gray-100">
              <h4 className="font-medium mb-3 text-gray-700">Included Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {planFeatures.map((feature, idx) => <div key={idx} className="flex items-start gap-2">
                    <Check size={16} className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </div>)}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            {showContactSales ? <Button onClick={()=>console.log('hello')} className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Phone size={16} className="mr-2" />
                Contact Sales Team
              </Button> : onEdit && status !== 'active' && <Button onClick={onEdit} className="w-full sm:w-auto" disabled={!isActive}>
                <CreditCard size={16} className="mr-2" />
                Activate Plan
              </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
};

export default PlanCard;